import { useEffect, useMemo, useState } from "react";
import platformAPI from "../../../services/platformApi";
import L from "../../../constants/baysportLabels";
import { formatRub } from "./PlatformDashboardParts";
import PlatformPageHeader from "./PlatformPageHeader";
import s from "./platformAdmin.module.css";

const CATEGORY_LABEL = {
  club: "Управление клубом",
  personalization: "Персонализация",
  social: "Сообщество",
  finance: "Финансы",
  communications: "Коммуникации",
  team: "Команда",
  analytics: "Аналитика",
  infrastructure: "Инфраструктура",
  support: "Поддержка",
  limits: "Лимиты",
  core: "Основное",
  growth: "Рост",
};

const EMPTY_PLAN = {
  code: "",
  name: "",
  description: "",
  monthly_price_rub: 0,
  yearly_price_rub: "",
  badge: "",
  sort_order: 0,
  is_public: true,
  is_active: true,
  legacy_plan_tier: "",
  features: {},
};

function defaultFeaturesFromCatalog(catalog = []) {
  const out = {};
  for (const f of catalog) {
    out[f.key] = f.type === "number" ? f.default : !!f.default;
  }
  return out;
}

export default function PlatformPlans() {
  const [plans, setPlans] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PLAN);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = () => {
    Promise.all([platformAPI.getPlans(), platformAPI.getFeaturesCatalog()])
      .then(([plansRes, featRes]) => {
        setPlans(plansRes.plans || []);
        setCatalog(featRes.features || []);
      })
      .catch((e) =>
        setErr(e.response?.data?.error || "Ошибка загрузки тарифов"),
      );
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!editing) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [editing]);

  const groupedFeatures = useMemo(() => {
    const groups = {};
    for (const f of catalog) {
      const cat = f.category || "core";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(f);
    }
    return groups;
  }, [catalog]);

  const openCreate = () => {
    setEditing("new");
    setForm({
      ...EMPTY_PLAN,
      features: defaultFeaturesFromCatalog(catalog),
    });
    setMsg("");
    setErr("");
  };

  const openEdit = (plan) => {
    setEditing(plan.id);
    setForm({
      code: plan.code,
      name: plan.name,
      description: plan.description || "",
      monthly_price_rub: plan.monthly_price_rub,
      yearly_price_rub: plan.yearly_price_rub ?? "",
      badge: plan.badge || "",
      sort_order: plan.sort_order || 0,
      is_public: plan.is_public,
      is_active: plan.is_active,
      legacy_plan_tier: plan.legacy_plan_tier || "",
      features: { ...plan.features },
    });
    setMsg("");
    setErr("");
  };

  const setFeature = (key, value) => {
    setForm((prev) => ({
      ...prev,
      features: { ...prev.features, [key]: value },
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");
    try {
      const body = {
        ...form,
        yearly_price_rub:
          form.yearly_price_rub === "" ? null : Number(form.yearly_price_rub),
        legacy_plan_tier: form.legacy_plan_tier || null,
        badge: form.badge || null,
      };
      if (editing === "new") {
        await platformAPI.createPlan(body);
        setMsg("Тариф создан");
      } else {
        const { code: _c, ...patch } = body;
        await platformAPI.updatePlan(editing, patch);
        setMsg("Тариф сохранён");
      }
      setEditing(null);
      load();
    } catch (e2) {
      setErr(e2.response?.data?.error || "Ошибка сохранения");
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Деактивировать тариф?")) return;
    try {
      await platformAPI.updatePlan(id, { is_active: false, is_public: false });
      load();
    } catch (e) {
      setErr(e.response?.data?.error || "Ошибка");
    }
  };

  return (
    <div className={s.page}>
      <PlatformPageHeader
        mobileOnlyBack
        backTo="/admin/more"
        backLabel="Ещё"
        title="Тарифы"
        lead={`Планы и функции для клубов ${L.productName}.`}
        actions={
          <button type="button" className={s.heroActionBtn} onClick={openCreate}>
            + Новый тариф
          </button>
        }
      />

      {msg ? <div className={s.alertSuccess}>{msg}</div> : null}
      {err ? <div className={s.alertError}>{err}</div> : null}

      <section className={s.planCardsGrid}>
        {plans.map((plan) => (
          <article
            key={plan.id}
            className={`${s.planOfferCard} ${!plan.is_active ? s.planOfferCardOff : ""}`}
          >
            <div className={s.planOfferHead}>
              <div>
                <h3 className={s.planOfferName}>{plan.name}</h3>
                <code className={s.planOfferCode}>{plan.code}</code>
              </div>
              {plan.badge ? (
                <span className={s.planBadgeSoft}>{plan.badge}</span>
              ) : null}
            </div>
            <div className={s.planOfferPrice}>
              <strong>{formatRub(plan.monthly_price_rub)} ₽</strong>
              <span>/ мес</span>
            </div>
            {plan.yearly_price_rub ? (
              <p className={s.infoMuted}>
                Год: {formatRub(plan.yearly_price_rub)} ₽
              </p>
            ) : null}
            {plan.description ? (
              <p className={s.planOfferDesc}>{plan.description}</p>
            ) : null}
            <div className={s.planOfferFlags}>
              <span
                className={`${s.statusPill} ${plan.is_public ? s.statusActive : s.statusNeutral}`}
              >
                {plan.is_public ? "На лендинге" : "Скрыт"}
              </span>
              <span
                className={`${s.statusPill} ${plan.is_active ? s.statusActive : s.statusSuspended}`}
              >
                {plan.is_active ? "Активен" : "Выкл"}
              </span>
            </div>
            <div className={s.formActions}>
              <button
                type="button"
                className={s.heroActionBtn}
                onClick={() => openEdit(plan)}
              >
                Изменить
              </button>
              {plan.is_active ? (
                <button
                  type="button"
                  className={s.heroActionBtnGhost}
                  onClick={() => deactivate(plan.id)}
                >
                  Выключить
                </button>
              ) : null}
            </div>
          </article>
        ))}
      </section>

      {editing ? (
        <div
          className={s.sheetOverlay}
          role="presentation"
          onClick={() => setEditing(null)}
        >
          <form
            className={s.sheetPanel}
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={s.sheetHead}>
              <h2 className={s.sheetTitle}>
                {editing === "new" ? "Новый тариф" : "Изменение тарифа"}
              </h2>
              <button
                type="button"
                className={s.sheetClose}
                onClick={() => setEditing(null)}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            <div className={s.sheetBody}>
              <div className={s.formGrid}>
                {editing === "new" ? (
                  <label className={s.field}>
                    <span>Код (латиница)</span>
                    <input
                      value={form.code}
                      onChange={(e) =>
                        setForm({ ...form, code: e.target.value })
                      }
                      required
                    />
                  </label>
                ) : null}
                <label className={s.field}>
                  <span>Название</span>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </label>
                <label className={s.field}>
                  <span>Цена / мес (₽)</span>
                  <input
                    type="number"
                    min="0"
                    value={form.monthly_price_rub}
                    onChange={(e) =>
                      setForm({ ...form, monthly_price_rub: e.target.value })
                    }
                  />
                </label>
                <label className={s.field}>
                  <span>Цена / год (₽)</span>
                  <input
                    type="number"
                    min="0"
                    value={form.yearly_price_rub}
                    onChange={(e) =>
                      setForm({ ...form, yearly_price_rub: e.target.value })
                    }
                  />
                </label>
                <label className={s.field}>
                  <span>Бейдж</span>
                  <input
                    value={form.badge}
                    onChange={(e) =>
                      setForm({ ...form, badge: e.target.value })
                    }
                    placeholder="Популярный"
                  />
                </label>
                <label className={s.field}>
                  <span>Порядок</span>
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) =>
                      setForm({ ...form, sort_order: e.target.value })
                    }
                  />
                </label>
              </div>

              <label className={s.field}>
                <span>Описание</span>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </label>

              <div className={s.toggleRows}>
                <label className={s.toggleRow}>
                  <span>Показывать на лендинге</span>
                  <input
                    type="checkbox"
                    checked={form.is_public}
                    onChange={(e) =>
                      setForm({ ...form, is_public: e.target.checked })
                    }
                  />
                </label>
                <label className={s.toggleRow}>
                  <span>Активен</span>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) =>
                      setForm({ ...form, is_active: e.target.checked })
                    }
                  />
                </label>
              </div>

              {Object.entries(groupedFeatures).map(([cat, items]) => (
                <div key={cat} className={s.featureGroup}>
                  <h3 className={s.featureGroupTitle}>
                    {CATEGORY_LABEL[cat] || cat}
                  </h3>
                  <div className={s.toggleRows}>
                    {items.map((f) => (
                      <label key={f.key} className={s.toggleRow}>
                        <span>{f.name}</span>
                        {f.type === "number" ? (
                          <input
                            type="number"
                            min="0"
                            className={s.toggleNumber}
                            value={form.features[f.key] ?? f.default}
                            onChange={(e) =>
                              setFeature(f.key, Number(e.target.value))
                            }
                          />
                        ) : (
                          <input
                            type="checkbox"
                            checked={!!form.features[f.key]}
                            onChange={(e) =>
                              setFeature(f.key, e.target.checked)
                            }
                          />
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className={s.sheetFooter}>
              <button
                type="button"
                className={s.heroActionBtnGhost}
                onClick={() => setEditing(null)}
              >
                Отмена
              </button>
              <button type="submit" className={s.heroActionBtn}>
                Сохранить
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
